import { memberIdentityOf } from '../pedigree-sidebar/memberIdentity';

// WHAT THE BULK EDITOR'S "SAVE ALL CHANGES" DOES TO THE CHART'S ROWS.
//
// The modal is handed the SELECTED people when more than one is selected on
// the canvas (ChartBox: `memberRecords.filter((m) => selectedIds.has(m.pid))`),
// and the chart's own setMemberRecords. Save used to build the new rows from
// that list and hand them to the setter whole, which replaced the family with
// the selection: everyone else vanished from the chart, and the next pedigree
// autosave POSTed only the selection. The server keeps the other people's
// documents, but it REPLACES the family's stored topology (pedigreeconfigs)
// with what was posted, and the chart trusts that on open: everyone left out
// reopened with no node, so with no parents, partners or children drawn — the
// "partial topology" state detect-partial-pedigree-config reports.
//
// It also built from the rows as they were when Save was clicked, then awaited
// every record write before setting them, so a row patched in between (a bulk
// Test save's genotype read-back) was overwritten with its older self.
//
// So each change is recorded as a function of the row, keyed by the person
// (memberIdentityOf: `_id`, never the pid), and applied — in the order it was
// made — to the chart's rows as they are when the setter runs. People it does
// not name are left exactly as they are.

/**
 * @returns {{
 *   add: (member: object, update: (row: object) => object) => void,
 *   size: number,
 *   applyTo: (records: Array<object>) => Array<object>
 * }}
 */
export function createRowUpdates() {
  const byPerson = new Map();

  return {
    /** Queue `update` for `member`'s row. */
    add(member, update) {
      const identity = memberIdentityOf(member);
      if (identity == null) {
        // Cannot happen for a chart row (a new member is minted a uid), and
        // matching on anything weaker could write onto someone else.
        console.warn(
          'Bulk edit: a change for a row with no identity was skipped'
        );
        return;
      }
      if (!byPerson.has(identity)) byPerson.set(identity, []);
      byPerson.get(identity).push(update);
    },

    get size() {
      return byPerson.size;
    },

    /**
     * The records with every queued change applied to its person's row.
     * Returns `records` itself when there is nothing to apply.
     */
    applyTo(records) {
      if (!Array.isArray(records) || byPerson.size === 0) return records;
      let changed = false;
      const next = records.map((row) => {
        const updates = byPerson.get(memberIdentityOf(row));
        if (!updates) return row;
        changed = true;
        return updates.reduce((current, update) => update(current), row);
      });
      return changed ? next : records;
    }
  };
}
